import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound } from "lucide-react";

export default function Login() {
  const { login, register, user, ready } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/");
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async () => {
    if (!forgotEmail) return toast.error("Enter your email address");
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If that email is registered, a reset link is on its way. Check your inbox.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 bg-background">
        <div className="max-w-sm w-full mx-auto">
          <div className="mb-10">
            <p className="text-xs tracking-[0.25em] uppercase text-accent font-semibold mb-3">Unified Transaction Hub</p>
            <h1 className="text-4xl sm:text-5xl text-foreground leading-tight">Guiding every home to <span className="italic text-primary">the closing table.</span></h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              A shared, deadline-aware workflow for listing agents, buyer agents and their transaction coordinators.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs tracking-widest uppercase text-muted-foreground">Full name</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  required className="mt-1.5 bg-card" placeholder="Jane Realtor" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase text-muted-foreground">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 bg-card" placeholder="you@brokerage.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-widest uppercase text-muted-foreground">Password</Label>
              <Input id="password" data-testid="auth-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 bg-card" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-accent" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full gap-2">
              <KeyRound className="w-4 h-4" />
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
            {mode === "login" && (
              <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                className="block text-sm text-muted-foreground hover:text-primary underline underline-offset-4">
                Forgot password?
              </button>
            )}
          </form>

          <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
            <DialogContent className="bg-card" data-testid="forgot-password-dialog">
              <DialogHeader><DialogTitle className="font-heading text-2xl text-primary">Reset your password</DialogTitle></DialogHeader>
              <DialogDescription className="text-sm text-muted-foreground">Enter your account email and we'll send you a secure link to set a new password. The link expires in 1 hour.</DialogDescription>
              <div>
                <Label className="text-xs tracking-widest uppercase text-muted-foreground">Email</Label>
                <Input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="mt-1.5 bg-card" placeholder="you@brokerage.com" />
              </div>
              <DialogFooter>
                <Button data-testid="forgot-submit-button" disabled={forgotLoading} onClick={sendReset} className="bg-primary text-primary-foreground rounded-full">
                  {forgotLoading ? "Sending…" : "Send reset link"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <p className="text-sm text-muted-foreground mt-6">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold underline underline-offset-4">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src="https://static.prod-images.emergentagent.com/jobs/4f6851a8-afe0-4aee-910d-4affb50454b8/images/fff07c864fcc1d539ab91a5f0b3a99e1c3f802ada75a89f1a8f53e850c866132.jpeg"
          alt="Two agents with a SOLD sign reviewing the transaction hub" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-primary/10" />
      </div>
    </div>
  );
}
